import useModalsStore from "../../store/modalsStore";
import NewTaskModal from "../NewTaskModal/NewTaskModal";
import NewLinkModal from "../NewLinkModal/NewLinkModal";
import ConfirmDialog from "../ConfirmDialog/ConfirmDialog";
import useHistoryStore from "../../store/historyStore";
import { useNavigate } from "react-router-dom";
import CanvasModal from "./CanvasModal";
import MoneyTransferModal from "src/pages/NewTransaction/MoneyTransferModal";
import TransactionsSearchModal from "./TransactionsSearchModal";
import TableSettingsModal from "./TableSettingsModal";
import useCurrentUser from "src/hooks/useCurrentUser";
import useAllTransactions from "src/hooks/useAllTransactions";
import CurrentUserQrContact from "../CurrentUserQrContact/CurrentUserQrContact";
import RegisterPassKeyModal from "../RegisterPassKeyModal/RegisterPassKeyModal";
import ChangePasswordModal from "../ChangePasswordModal/ChangePasswordModal";
import { useMemo, useTransition } from "react";
import NotificationSettings from "../NotificationSettings/NotificationSettings";
import CurrentUserPrivacyModal from "../CurrentUserPrivacyModal/CurrentUserPrivacyModal";
import LanguageChangeModal from "../LanguageChangeModal/LanguageChangeModal";
import { useTranslation } from "react-i18next";
import { ROUTES } from "src/helpers/routes";
import ChartSettingsModal from "./ChartSettingsModal";
import EmailSettingsModal from "./EmailSettingsModal";
import SearchNetworkModal from "./SearchNetworkModal";
import GmailConnectorModal from "../Integrations/GmailConnectorA/GmailConnectorModal";
import { openExternalLink } from "src/helpers/utils";
import { FEEDBACK_FORM_URL } from "src/helpers/constants";
import EmailInviteModal from "../EmailInviteModal/EmailInviteModal";
import OpenAiConnectorModal from "../Integrations/OpenAiApi/OpenAiConnectorModal";
import GlobalAccountsSettings from "./GlobalAccountsSettings";
import TransactionSettingsModal from "./TransactionSettingsModal";
import temporalStore from "src/store/temporalStore";
import AccountModal from "src/pages/Accounts/AccountModal";
import ThemeSettingsModal from "./ThemeSettingsModal";
import EditCategoryModal from "src/pages/Categories/EditCategoryModal";
import EditClientProviderModal from "src/pages/Sources/EditClientProviderModal";
import useMainFilter from "src/hooks/useMainFilter";
import useSessionPreferences from "src/store/useSessionPreferences";
import PaginationSettingsModal from "./PaginationSettingsModal";
import FrequentTransactionModal from "./FrequentTransactionModal";
import { EButtonKind } from "../Button/Button.types";
import DayColorsModal from "src/pages/Colors/DayColorsModal";
import MonthColorsModal from "src/pages/Colors/MonthColorsModal";
import RecurringTransactionPayment from "../RecurringTransaction/RecurringTransactionPayment";

const ModalsLoader: React.FC = () => {
  const { t } = useTranslation();
  const { isLoadingAccounts } = useCurrentUser();
  const { isLoadingAllTransactions } = useAllTransactions();
  const navigate = useNavigate();
  const { setPreselectedAccountForTransfer } = temporalStore();
  const {
    setShowCreateNewAccountModal,
    showCreateNewAccountModal,
    setShowCreateNewTask,
    showCreateNewTask,
    showCreateNewLink,
    setShowCreateNewLink,
    setShowEditProfile,
    showEditProfile,
    showEditAccount,
    setShowEditAccount,
    showDeleteHistoryConfirm,
    setShowDeleteHistoryConfirm,
    setShowLogoutDialog,
    showLogoutDialog,
    showCreateNewCanvas,
    setShowCreateNewCanvas,
    showMoneyTransferModal,
    setShowMoneyTransferModal,
    showSearchModal,
    setShowSearchModal,
    showTableSettingsModal,
    setShowTableSettingsModal,
    showContactQrCode,
    showCreatePassKey,
    setShowCreatePassKey,
    showChangePassword,
    setShowChangePassword,
    showNotificationsSettings,
    setShowNotificationsSettings,
    showPrivacySettings,
    setShowPrivacySettings,
    showLanguageChange,
    setShowLanguageChange,
    showChartSettingsModal,
    showEmailNotificationsSettingsModal,
    setShowEmailNotificationsSettingsModal,
    showContactSearchModal,
    setShowContactSearchModal,
    integrations: { showGmailConnectorModal, showOpenAiApiModal },
    setShowFeedbackConfirmDialog,
    showFeedbackConfirmDialog,
    showEmailInviteModal,
    showAccountsSettingsModal,
    showTransactionSettingsModal,
    setShowEditAccountModal,
    editAccountModalPayload,
    showEditAccountModal,
    showThemeSettingsModal,
    showEditCategoryModal,
    editCategoryModalPayload,
    showEditClientProviderModal,
    editClientProviderModalPayload,
    setShowCreateRecurringTransactionModal,
    showCreateRecurringTransactionModal,
    showPaginationSettingsModal,
    editRecurringTransactionPayload,
    showEditRecurringTransactionModal,
    showDayColorsModal,
    showMonthColorsModal,
    showRecurringTransactionPaymentModal,
    recurringTransactionPaymentPayload,
  } = useModalsStore();
  const resetSessionPreferences = useSessionPreferences(
    (s) => s.resetSessionPreferences,
  );

  const { clearHistory } = useHistoryStore();
  const [isPending, startTransition] = useTransition();
  const { mainFilter, setMainFilter } = useMainFilter();

  const isLoadingSomething = useMemo(
    () => [!!isLoadingAccounts, !!isLoadingAllTransactions].includes(true),
    [isLoadingAccounts, isLoadingAllTransactions],
  );

  if (isLoadingSomething) return null;

  return (
    <>
      {showCreateNewTask && (
        <NewTaskModal
          onClose={() => startTransition(() => setShowCreateNewTask(false))}
        />
      )}

      {showCreateNewLink && (
        <NewLinkModal
          onClose={() => startTransition(() => setShowCreateNewLink(false))}
        />
      )}

      {showDeleteHistoryConfirm && (
        <ConfirmDialog
          description="¿Deseas limpiar tu historial?"
          title="Limpiar historial"
          onConfirm={() =>
            startTransition(() => {
              clearHistory();
              setShowDeleteHistoryConfirm(false);
            })
          }
          onClose={() =>
            startTransition(() => setShowDeleteHistoryConfirm(false))
          }
          confirmKind={EButtonKind.DANGER}
        />
      )}

      {showLogoutDialog && (
        <ConfirmDialog
          description={t("dialogs:questions.logout")}
          onClose={() => startTransition(() => setShowLogoutDialog(false))}
          onConfirm={() =>
            startTransition(() => {
              setShowLogoutDialog(false);
              resetSessionPreferences();
              navigate(ROUTES.LOGOUT);
            })
          }
          title={t("dialogs:titles.exit")}
        />
      )}

      {showCreateNewCanvas && (
        <CanvasModal
          onClose={() => startTransition(() => setShowCreateNewCanvas(false))}
        />
      )}

      {showMoneyTransferModal && (
        <MoneyTransferModal
          disableFields={false}
          onClose={() =>
            startTransition(() => {
              setShowMoneyTransferModal(false);
              setPreselectedAccountForTransfer("");
            })
          }
          onSubmit={() => {
            startTransition(() => {
              setShowMoneyTransferModal(false);
              setPreselectedAccountForTransfer("");
            });
          }}
        />
      )}

      {showSearchModal && (
        <TransactionsSearchModal
          onClose={() => startTransition(() => setShowSearchModal(false))}
          onSubmit={(filterOptions) =>
            startTransition(() => {
              setMainFilter({
                ...mainFilter,
                ...filterOptions,
              });
              setShowSearchModal(false);
            })
          }
        />
      )}

      {showTableSettingsModal && <TableSettingsModal />}

      {showPaginationSettingsModal && <PaginationSettingsModal />}

      {showChartSettingsModal && <ChartSettingsModal />}

      {showContactQrCode && <CurrentUserQrContact />}

      {showCreatePassKey && (
        <RegisterPassKeyModal
          onClose={() => startTransition(() => setShowCreatePassKey(false))}
        />
      )}

      {showChangePassword && (
        <ChangePasswordModal
          onClose={() => startTransition(() => setShowChangePassword(false))}
        />
      )}

      {showNotificationsSettings && (
        <NotificationSettings
          showSubscriptionMessage
          onClose={() => setShowNotificationsSettings(false)}
        />
      )}

      {showEmailNotificationsSettingsModal && (
        <EmailSettingsModal
          onClose={() => setShowEmailNotificationsSettingsModal(false)}
        />
      )}

      {showPrivacySettings && (
        <CurrentUserPrivacyModal
          onClose={() => setShowPrivacySettings(false)}
        />
      )}

      {showLanguageChange && (
        <LanguageChangeModal onClose={() => setShowLanguageChange(false)} />
      )}

      {showContactSearchModal && (
        <SearchNetworkModal onClose={() => setShowContactSearchModal(false)} />
      )}

      {showGmailConnectorModal && <GmailConnectorModal />}
      {showOpenAiApiModal && <OpenAiConnectorModal />}

      {showFeedbackConfirmDialog && (
        <ConfirmDialog
          description="Se abrirá una página externa con un formulario de Google Forms para que puedas proporcionar tu feedback ¿Deseas continuar?"
          onClose={() => setShowFeedbackConfirmDialog(false)}
          onConfirm={() => {
            openExternalLink(FEEDBACK_FORM_URL);
            setShowFeedbackConfirmDialog(false);
          }}
          title="Feedback"
        />
      )}

      {showEmailInviteModal && <EmailInviteModal />}

      {showCreateNewAccountModal && <AccountModal />}

      {showEditAccountModal && (
        <AccountModal {...(editAccountModalPayload || {})} />
      )}

      {showEditRecurringTransactionModal && (
        <FrequentTransactionModal
          {...(editRecurringTransactionPayload || {})}
        />
      )}

      {showEditCategoryModal && (
        <EditCategoryModal {...(editCategoryModalPayload as any)} />
      )}

      {showEditClientProviderModal && (
        <EditClientProviderModal {...(editClientProviderModalPayload as any)} />
      )}

      {showAccountsSettingsModal && <GlobalAccountsSettings />}

      {showTransactionSettingsModal && <TransactionSettingsModal />}

      {showThemeSettingsModal && <ThemeSettingsModal />}

      {showCreateRecurringTransactionModal && <FrequentTransactionModal />}

      {showDayColorsModal && <DayColorsModal />}

      {showMonthColorsModal && <MonthColorsModal />}

      {showRecurringTransactionPaymentModal && (
        <RecurringTransactionPayment
          {...(recurringTransactionPaymentPayload as any)}
        />
      )}
    </>
  );
};

export default ModalsLoader;
